import { Target } from "lucide-react";
import { cn } from "@wa/ui";
import { StatusPill } from "../../../ui";
import {
  CRITICALITY_BAR,
  CRITICALITY_TEXT,
  criticalityBand,
  type SiteProfile,
} from "../../../../lib/siteScan";
import type { T } from "../types";
import { Panel } from "./primitives";

/**
 * Why this score, and not the one the defects add up to.
 *
 * The scanner used to show a single number and an operator had no way to argue
 * with it. This panel is the argument: what the site is for, how much of the
 * business runs through it, and the multiplication that turned the defect count
 * into the priority. A site that lost 30 points to "one page, no conversion
 * path, nothing measured" should say so in those words, because that sentence
 * is also the reason not to spend an afternoon on the call.
 */

/** The multiplication, spelled out. An operator has to be able to redo it. */
function Arithmetic({ defect, factor, score, t }: { defect: number; factor: number; score: number; t: T }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-3 text-sm">
      <span className="crm-tnum font-semibold">{defect}</span>
      <span className="text-[11px] text-muted-foreground">{t("scanner.profile.defect")}</span>
      <span className="text-muted-foreground">x</span>
      <span className="crm-tnum font-semibold">{factor.toFixed(2)}</span>
      <span className="text-[11px] text-muted-foreground">{t("scanner.profile.factor")}</span>
      <span className="text-muted-foreground">=</span>
      <span className="crm-tnum text-base font-bold">{score}</span>
      <span className="text-[11px] text-muted-foreground">{t("scanner.profile.result")}</span>
    </div>
  );
}

/** Criticality as a bar. 0-100, with the band's own colour. */
function CriticalityMeter({ value, t }: { value: number; t: T }) {
  const band = criticalityBand(value) ?? "low";
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium text-muted-foreground">{t("scanner.profile.criticality")}</span>
        <span className={cn("crm-tnum text-sm font-bold", CRITICALITY_TEXT[band])}>{value}</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted/45">
        <div
          className={cn("h-full rounded-full transition-[width] duration-700 ease-out", CRITICALITY_BAR[band])}
          style={{ width: `${Math.max(2, Math.min(100, value))}%` }}
        />
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">{t(`scanner.profile.band.${band}`)}</p>
    </div>
  );
}

export function ProfilePanel({
  profile,
  defectScore,
  opportunityScore,
  t,
}: {
  profile: SiteProfile;
  defectScore: number;
  opportunityScore: number;
  t: T;
}) {
  // Weight 0 is a named absence ("one page", "nothing measured"), not a signal
  // that happened to score nothing. They are worth showing and worth showing
  // apart: the first list is what the site has, the second is the explanation.
  const present = profile.signals.filter((s) => s.weight > 0);
  const absent = profile.signals.filter((s) => s.weight === 0);

  return (
    <Panel
      icon={Target}
      title={t("scanner.profile.title")}
      aside={<StatusPill tone="info">{t(`scanner.kind.${profile.kind}`)}</StatusPill>}
    >
      <div className="space-y-3.5">
        <p className="text-sm text-muted-foreground">{t(`scanner.kind.about.${profile.kind}`)}</p>

        <CriticalityMeter value={profile.criticality} t={t} />

        <Arithmetic defect={defectScore} factor={profile.factor} score={opportunityScore} t={t} />

        {present.length > 0 && (
          <div className="border-t border-[var(--crm-hair)] pt-3">
            <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">
              {t("scanner.profile.signals")}
            </p>
            <ul className="space-y-1">
              {present.map((s) => (
                <li key={s.id} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">{t(`scanner.signal.${s.id}`, s.vars)}</span>
                  <span className="crm-tnum shrink-0 text-xs font-semibold text-muted-foreground">+{s.weight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {absent.length > 0 && (
          <div className="border-t border-[var(--crm-hair)] pt-3">
            <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">
              {t("scanner.profile.absences")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {absent.map((s) => (
                <StatusPill key={s.id} tone="mut">
                  {t(`scanner.signal.${s.id}`, s.vars)}
                </StatusPill>
              ))}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
